import {
  ButtonHierarchy,
  ButtonSize,
  ButtonType,
  MainButton,
} from "@beydesign/storybook"
import { Box } from "@mui/material"
import * as Sentry from "@sentry/react"
import { getAuth } from "firebase/auth"
import React, { useEffect, useState } from "react"
import { useAppContext, useAppDispatchContext } from "../../app/Context"
import { StorageKeys } from "../../constants"
import { ExcludedDomains, ExcludedEmails } from "../../constants/emails"
import { updateMixpanelUser } from "../../external/analytics"
import { useStorage } from "../../hooks"
import { StateAction } from "../../types/State"
import { SnackbarType } from "../../types/UI"
import { User } from "../../types/User"
import { isValidEmail } from "../../utils/func"
import { CardScrim } from "../CallChrome/CardScrim"
import { ChatWithAgent } from "../CustomVideoConference/ChatWithAgent"
import { MediaSetupControls } from "../MediaControlsBar/MediaSetupControls"
import { SnackbarWithText } from "../snackbar"

const isBlacklistedEmail = (email: string): boolean => {
  const lowerEmail = email.toLowerCase()

  if (
    ExcludedEmails.some((excluded) => excluded.toLowerCase() === lowerEmail)
  ) {
    return true
  }

  return ExcludedDomains.some((domain) =>
    lowerEmail.endsWith(`@${domain.toLowerCase()}`),
  )
}

const isExcludedFromHubspot = (
  email: string,
  hasCustomBranding: boolean,
): boolean => {
  if (hasCustomBranding) {
    return true
  }

  return isBlacklistedEmail(email)
}

const fieldSx = {
  width: "100%",
  padding: "12px 16px",
  borderRadius: "var(--spacing-tokens-size-in-px-radius-radius-md)",
  border: "1px solid var(--color-border-border-component)",
  backgroundColor: "var(--color-background-bg-component)",
  color: "var(--color-text-text-title)",
  fontFamily: "var(--app-font-family, figtree, sans-serif)",
  fontSize: "var(--global-text-m-regular-font-size)",
  outline: "none",
  "&::placeholder": { color: "var(--color-text-text-placeholder)" },
  "&:focus": { borderColor: "var(--color-border-border-brand)" },
}

interface UserLoginModalProps {
  open: boolean
  onClose?: () => void
  onJoinCall?: () => void
}

export const UserLoginModal: React.FC<UserLoginModalProps> = ({
  open,
  onClose,
  onJoinCall,
}) => {
  const [firstName, setFirstName] = useState<string>("")
  const [lastName, setLastName] = useState<string>("")
  const [email, setEmail] = useState<string>("")
  const [company, setCompany] = useState<string>("")
  const [errorMessage, setErrorMessage] = useState<string>("")

  const { setItem, getItem } = useStorage()
  const dispatch = useAppDispatchContext()
  const context = useAppContext()
  const auth = getAuth()

  useEffect(() => {
    if (!open) return

    const anonEmail = getItem<string | null>(StorageKeys.ANON_EMAIL)
    const anonName = getItem<string | null>(StorageKeys.ANON_NAME)
    const anonFirstName = getItem<string | null>(StorageKeys.ANON_FIRST_NAME)
    const anonLastName = getItem<string | null>(StorageKeys.ANON_LAST_NAME)
    const anonCompany = getItem<string | null>(StorageKeys.ANON_COMPANY)
    if (anonFirstName && anonLastName && anonEmail && isValidEmail(anonEmail)) {
      setFirstName(anonFirstName)
      setLastName(anonLastName)
      setEmail(anonEmail)
      if (anonCompany) {
        setCompany(anonCompany)
      }
    } else if (anonName && anonEmail && isValidEmail(anonEmail)) {
      const [first, ...rest] = anonName.split(" ")
      setFirstName(first)
      setLastName(rest.join(" "))
      setEmail(anonEmail)
      if (anonCompany) {
        setCompany(anonCompany)
      }
    }
  }, [open])

  const handleAnonLogin = async () => {
    try {
      if (firstName === "") {
        setErrorMessage("First name is required")
        return
      }
      if (lastName === "") {
        setErrorMessage("Last name is required")
        return
      }
      if (!isValidEmail(email)) {
        setErrorMessage("Invalid email")
        return
      }
      const fullName = `${firstName} ${lastName}`
      const user: User = {
        ...context.user,
        // The LiveKit token is minted with the Firebase uid, so a locally
        // generated id would never match what the backend reports.
        uid:
          context.user?.uid ||
          auth.currentUser?.uid ||
          `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        email,
        name: fullName,
        firstName,
        lastName,
        company: company || undefined,
      }

      updateMixpanelUser(user)

      setItem(StorageKeys.ANON_EMAIL, email)
      setItem(StorageKeys.ANON_NAME, fullName)
      setItem(StorageKeys.ANON_FIRST_NAME, firstName)
      setItem(StorageKeys.ANON_LAST_NAME, lastName)
      if (company) {
        setItem(StorageKeys.ANON_COMPANY, company)
      }
      dispatch({ type: StateAction.setUser, payload: user })
      onJoinCall?.()
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "An unexpected error occurred",
      )
      Sentry.captureException(error)
    }
  }

  if (!open) return null

  const excludedFromHubspot = isExcludedFromHubspot(
    email,
    !!context.agent?.custom_branding,
  )

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    handleAnonLogin()
  }

  return (
    <CardScrim>
      {onClose && (
        <ChatWithAgent
          agent={context.agent}
          sx={{
            top: "var(--spacing-tokens-size-in-px-spacing-spacing-xl)",
            left: "var(--spacing-tokens-size-in-px-spacing-spacing-xl)",
          }}
          onBackButtonClick={onClose}
        />
      )}
      <SnackbarWithText
        open={errorMessage !== ""}
        severity={SnackbarType.error}
        text={errorMessage}
        handleClose={() => setErrorMessage("")}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "var(--spacing-tokens-size-in-px-spacing-spacing-4xl)",
          width: "100%",
          maxWidth: 400,
        }}
      >
        <Box
          sx={{
            fontFamily: "var(--app-font-family, figtree, sans-serif)",
            color: "var(--color-text-text-white)",
          }}
        >
          <Box
            sx={{
              fontSize: {
                xs: "var(--global-heading-m-semi-bold-font-size)",
                sm: "var(--global-heading-l-semi-bold-font-size)",
              },
              fontWeight: "var(--global-heading-l-semi-bold-font-weight)",
              lineHeight:
                "calc(var(--global-heading-l-semi-bold-line-height) * 1px)",
            }}
          >
            Enter your details
          </Box>
          <Box
            sx={{
              fontSize: {
                xs: "var(--global-text-m-medium-font-size)",
                sm: "var(--global-text-l-medium-font-size)",
              },
              fontWeight: "var(--global-text-l-medium-font-weight)",
              lineHeight: "calc(var(--global-text-l-medium-line-height) * 1px)",
            }}
          >
            Hit 'Start Conversation' to begin!
          </Box>
        </Box>

        <Box
          component="form"
          name="contactForm"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--spacing-tokens-size-in-px-spacing-spacing-lg)",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: "var(--spacing-tokens-size-in-px-spacing-spacing-md)",
            }}
          >
            <Box
              component="input"
              type="text"
              name="firstName"
              placeholder="First Name*"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              sx={fieldSx}
            />
            <Box
              component="input"
              type="text"
              name="lastName"
              placeholder="Last Name*"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              sx={fieldSx}
            />
          </Box>
          <Box
            component="input"
            type="email"
            name="email"
            placeholder="Email Address*"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={fieldSx}
          />
          <Box
            component="input"
            type="text"
            name="company"
            placeholder="Company"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            sx={fieldSx}
          />
          {!excludedFromHubspot && (
            <input type="hidden" name="Source" value="Demo" readOnly />
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginTop: "var(--spacing-tokens-size-in-px-spacing-spacing-md)",
            }}
          >
            <MainButton
              type={excludedFromHubspot ? ButtonType.Button : ButtonType.Submit}
              onClick={excludedFromHubspot ? handleAnonLogin : undefined}
              hierarchy={ButtonHierarchy.Primary}
              size={ButtonSize.xl}
              text="Start Conversation"
              data-hs-ignore="true"
              id="call-contactform-start-conversation"
            />
          </Box>
        </Box>
      </Box>

      <MediaSetupControls />
    </CardScrim>
  )
}
